import { AppBar, Toolbar, Container, Typography, IconButton, Avatar, Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from "@mui/material";
import { Drawer, List, Collapse, Divider, ListItemButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import GroupIcon from "@mui/icons-material/Group";
import PaymentIcon from "@mui/icons-material/Payment";
import EventIcon from "@mui/icons-material/Event";
import ReportIcon from "@mui/icons-material/Assessment";
import EmailIcon from "@mui/icons-material/Email";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SettingsIcon from "@mui/icons-material/Settings";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { UserRole } from "../common/enums/userRole";
import { useStore } from "../stores/store";
import LanguageSelector from "./LanguageSelector";
import { useMediaQuery, useTheme } from "@mui/material";
import agent from "../api/agent";
import { toast } from "react-toastify";

const SIDEBAR_WIDTH = 270;
const SIDEBAR_COLLAPSED_WIDTH = 56;

const pageTitles: Record<string, string> = {
  "/home": "",
  "/members": "general.members",
  "/pending-members": "general.pendingMembers",
  "/create-member": "navbar.addMember",
  "/upload-members": "navbar.uploadMembers",
  "/payments": "navbar.payments",
  "/unlinked-payments": "navbar.unlinkedPayments",
  "/upload-payments": "navbar.uploadPayments",
  "/activities": "navbar.activities",
  "/create-activity": "navbar.addActivity",
  "/reports": "navbar.reports",
  "/unpaid-memberships": "navbar.unpaidMemberships",
  "/paid-memberships": "navbar.paidMemberships",
  "/emails/send": "emails.send.title",
  "/emails/batch-letters-history": "emails.batchLettersHistory.title",
  "/emails/templates": "emails.templates.title",
  "/new-member-notifications": "emails.dropdown.notificationEmails",
  "/emails/layout-settings": "emails.layout.title",
  "/emails/logs": "emails.logs.title",
  "/users-create": "navbar.addAdministrator",
  "/tenants-create": "navbar.addOrganization",
  "/my-profile": "navbar.myProfile",
  "/duplicate-members": "members.duplicateMembersTitle",
  "/tenant-settings": "navbar.settings",
};

export default function NavBar() {
  const location = useLocation();
  const { t } = useTranslation();
  const { userStore, uiStore } = useStore();
  const {
    userStore: { loggedUser, logout, userRole },
  } = useStore();

  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [backupLoading, setBackupLoading] = useState(false);

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      const blob = await agent.Backup.downloadDatabase();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.href = url;
      a.download = `database_backup_${timestamp}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("navbar.databaseBackupSuccess"));
    } catch {
      toast.error(t("navbar.databaseBackupError"));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDrawerToggle = () => uiStore.toggleSidebar();
  const handleExpandMenu = (name: string) =>
    setExpandedMenu(expandedMenu === name ? null : name);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const tenantName = loggedUser?.tenant?.title || loggedUser?.tenant?.name;
  const isLoggedIn = userStore.isLoggedIn;
  const mainContentMarginLeft = uiStore.sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <AppBar
        position="fixed"
        color="primary"
        sx={{
          zIndex: 1000,
          boxShadow: 3,
          background: "linear-gradient(90deg, #388e3c 0%, #a5d6a7 85%, #e6c200 97%, #fffde7 100%)",
          left: isLoggedIn && !isMobile ? mainContentMarginLeft : 0,
          width: { xs: "100%", sm: isLoggedIn && !isMobile ? `calc(100% - ${mainContentMarginLeft}px)` : "100%" },
          transition: "left 0.2s, width 0.2s",
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            pl: 0,
            pr: 0,
            minWidth: 0,
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 56, sm: 72 },
              px: { xs: 1, sm: 3 },
              bgcolor: "transparent",
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, flexShrink: 0 }}>
              {isLoggedIn && isMobile && (
                <IconButton
                  color="inherit"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 0.5 }}
                  aria-label="open drawer"
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Avatar
                src="/assets/logo.png"
                alt="logo"
                sx={{
                  width: { xs: 36, sm: 44 },
                  height: { xs: 36, sm: 44 },
                  mr: 1,
                  ml: 0,
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    letterSpacing: 2,
                    textShadow: "0 2px 8px #388e3c",
                    fontSize: { xs: "1.05rem", sm: "1.5rem" },
                    mr: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {tenantName}
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      fontWeight: 500,
                      letterSpacing: 1,
                      textShadow: "0 2px 8px #388e3c",
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      ml: 1,
                      whiteSpace: "nowrap",
                      mt: "5px",
                    }}
                  >
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Right: Profile select and language */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {loggedUser && (
                <>
                  <Button
                    color="inherit"
                    startIcon={<AccountCircleIcon />}
                    onClick={handleProfileMenuOpen}
                    sx={{
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 500,
                      py: 1,
                      mt: "8px",
                      '&:hover': { bgcolor: "rgba(255,255,255,0.18)" }
                    }}
                  >
                    {loggedUser?.username}
                  </Button>
                  <Menu
                    anchorEl={profileMenuAnchor}
                    open={Boolean(profileMenuAnchor)}
                    onClose={handleProfileMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem
                      component={Link}
                      to={`/my-profile`}
                      onClick={handleProfileMenuClose}
                    >
                      <ListItemIcon>
                        <AccountCircleIcon />
                      </ListItemIcon>
                      <ListItemText>{t("navbar.myProfile")}</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        logout();
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <AccountCircleIcon />
                      </ListItemIcon>
                      <ListItemText>{t("navbar.logout")}</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
              <LanguageSelector />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Sidebar navigation - visible only when logged in */}
      {isLoggedIn && (
        <Drawer
          anchor="left"
          open={uiStore.sidebarOpen}
          variant={isMobile ? "temporary" : "permanent"}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          slotProps={{
            paper: {
              sx: {
                width: uiStore.sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
                transition: "width 0.2s",
                overflowX: "hidden",
                bgcolor: "linear-gradient(135deg, #388e3c 0%, #a5d6a7 90%, #e6c200 98%, #fffde7 100%)",
                color: "white",
                background: "linear-gradient(135deg, #388e3c 0%, #a5d6a7 90%, #e6c200 98%, #fffde7 100%)",
                position: "fixed",
                left: 0,
                top: 0,
                height: "100vh",
                boxShadow: 3,
              },
            },
          }}
        >
          <Box sx={{ position: "relative", height: 70 }}>
            <IconButton
              color="success"
              onClick={handleDrawerToggle}
              sx={{
                position: "absolute",
                top: 15,
                left: 8,
                bgcolor: "white",
                boxShadow: 2,
                border: "2px solid #388e3c",
                zIndex: 1201,
                width: 40,
                height: 40,
              }}
            >
              <MenuIcon />
            </IconButton>
            {uiStore.sidebarOpen && (
              <Box sx={{ p: 2, marginLeft: "40px", marginTop: "5px", display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 2, color: "#fff", textShadow: "0 2px 8px #388e3c" }}>
                  Džemat App
                </Typography>
              </Box>
            )}
          </Box>
          <Divider sx={{ bgcolor: "white", opacity: 0.2, mb: 1 }} />
          <List sx={{ pt: 0 }}>
            {userRole === UserRole.ADMIN && (
              <>
                <ListItemButton component={NavLink} to="/home">
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><HomeIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.home")} />}
                </ListItemButton>
                <ListItemButton onClick={() => handleExpandMenu("members")}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><GroupIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.members")} />}
                  {uiStore.sidebarOpen && <ArrowDropDownIcon />}
                </ListItemButton>
                <Collapse in={expandedMenu === "members" && uiStore.sidebarOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/members" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.allMembers")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/pending-members" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.pendingMembers")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/create-member" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.addMember")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/upload-members" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.uploadMembers")} />
                    </ListItemButton>
                    {/* Add Duplicate Members link */}
                    <ListItemButton component={NavLink} to="/duplicate-members" sx={{ pl: 4 }}>
                      <ListItemText primary={t("members.duplicateMembersTitle") || "Duplicate Members"} />
                    </ListItemButton>
                  </List>
                </Collapse>
                <ListItemButton onClick={() => handleExpandMenu("payments")}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><PaymentIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.payments")} />}
                  {uiStore.sidebarOpen && <ArrowDropDownIcon />}
                </ListItemButton>
                <Collapse in={expandedMenu === "payments" && uiStore.sidebarOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/unlinked-payments" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.unlinkedPayments")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/upload-payments" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.uploadPayments")} />
                    </ListItemButton>
                  </List>
                </Collapse>
                <ListItemButton onClick={() => handleExpandMenu("activities")}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><EventIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.activities")} />}
                  {uiStore.sidebarOpen && <ArrowDropDownIcon />}
                </ListItemButton>
                <Collapse in={expandedMenu === "activities" && uiStore.sidebarOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/activities" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.activities")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/create-activity" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.addActivity")} />
                    </ListItemButton>
                  </List>
                </Collapse>
                <ListItemButton onClick={() => handleExpandMenu("reports")}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><ReportIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.reports")} />}
                  {uiStore.sidebarOpen && <ArrowDropDownIcon />}
                </ListItemButton>
                <Collapse in={expandedMenu === "reports" && uiStore.sidebarOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/unpaid-memberships" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.unpaidMemberships")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/paid-memberships" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.paidMemberships")} />
                    </ListItemButton>
                  </List>
                </Collapse>
                <ListItemButton onClick={() => handleExpandMenu("emails")}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><EmailIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("emails.dropdown.title")} />}
                  {uiStore.sidebarOpen && <ArrowDropDownIcon />}
                </ListItemButton>
                <Collapse in={expandedMenu === "emails" && uiStore.sidebarOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/emails/send" sx={{ pl: 4 }}>
                      <ListItemText primary={t("emails.dropdown.send")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/emails/batch-letters-history" sx={{ pl: 4 }}>
                      <ListItemText primary={t("emails.dropdown.batchLettersHistory")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/emails/templates" sx={{ pl: 4 }}>
                      <ListItemText primary={t("emails.dropdown.templates")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/new-member-notifications" sx={{ pl: 4 }}>
                      <ListItemText primary={t("emails.dropdown.notificationEmails")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/emails/layout-settings" sx={{ pl: 4 }}>
                      <ListItemText primary={t("emails.dropdown.layoutSettings")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/emails/logs" sx={{ pl: 4 }}>
                      <ListItemText primary={t("emails.dropdown.logs")} />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
            {userRole === UserRole.ADMIN && (
              <ListItemButton component={NavLink} to="/tenant-settings" sx={{ justifyContent: uiStore.sidebarOpen ? "initial" : "center", px: 2 }}>
                <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><SettingsIcon /></ListItemIcon>
                {uiStore.sidebarOpen && <ListItemText primary={t("navbar.settings")} />}
              </ListItemButton>
            )}
            {userRole === UserRole.SUPER_ADMIN && (
              <>
                <ListItemButton onClick={() => handleExpandMenu("administration")} sx={{ justifyContent: uiStore.sidebarOpen ? "initial" : "center", px: 2 }}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}><AdminPanelSettingsIcon /></ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.administration")} />}
                  {uiStore.sidebarOpen && <ArrowDropDownIcon />}
                </ListItemButton>
                <Collapse in={expandedMenu === "administration" && uiStore.sidebarOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/users-create" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.addAdministrator")} />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/tenants-create" sx={{ pl: 4 }}>
                      <ListItemText primary={t("navbar.addOrganization")} />
                    </ListItemButton>
                  </List>
                </Collapse>
                <ListItemButton onClick={handleDownloadBackup} disabled={backupLoading}>
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: uiStore.sidebarOpen ? 2 : "auto", justifyContent: "center" }}>
                    {backupLoading
                      ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                      : <CloudDownloadIcon />}
                  </ListItemIcon>
                  {uiStore.sidebarOpen && <ListItemText primary={t("navbar.databaseBackup")} />}
                </ListItemButton>
              </>
            )}
          </List>
          <Divider sx={{ bgcolor: "white", opacity: 0.2, mt: 1 }} />
        </Drawer>
      )}

      <Box
        sx={{
          ml: { xs: 0, sm: mainContentMarginLeft },
          transition: "margin-left 0.2s",
        }}
      >
      </Box>
    </>
  );
}
